import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useRBAC } from '../hooks/useRBAC';
import { useNotification } from '../hooks/useNotification';
import { Product } from 'public-contracts';
import { productsService } from '../services/productsService';
import { useListFetch } from '../hooks/useListFetch';
import { ListFetchStates } from '../components/common/ListFetchStates';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ProductCategory } from 'public-contracts';

// Lucide React icons
import {
  Plus,
  Edit,
  Search,
  Package,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  Trash2,
} from 'lucide-react';

interface ProductListProps {
  refreshTrigger?: number;
  onCreateClick: () => void;
  onEditClick: (product: Product) => void;
}

export const ModernProductList: React.FC<ProductListProps> = ({
  refreshTrigger,
  onCreateClick,
  onEditClick,
}) => {
  const { translate } = useTranslation();
  const { hasPermission } = useRBAC();
  const { notify } = useNotification();

  // Permission checks
  const canCreate = hasPermission('products', 'create');
  const canEdit = hasPermission('products', 'edit');
  const canDelete = hasPermission('products', 'delete');
  
  // Debug: Log permissions (remove in production)
  useEffect(() => {
    console.log('[ModernProductList] Permissions:', {
      canCreate,
      canEdit,
      canDelete,
    });
  }, [canCreate, canEdit, canDelete]);

  // Pagination state
  const { data, isLoading, isError, fetchData } = useListFetch<{
    products: Product[];
    total: number;
  }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20, // Items per page
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasLoadedOnceRef = useRef(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  // Filter state
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [filters, setFilters] = useState<{
    categoryId?: string;
    recipeType?: 'recipe' | 'stock' | 'all';
    currency?: string;
    isActive?: boolean | 'all';
  }>({
    categoryId: undefined,
    recipeType: 'all',
    currency: undefined,
    isActive: 'all',
  });
  const [filterForm, setFilterForm] = useState({
    categoryId: 'all',
    recipeType: 'all',
    currency: 'all',
    isActive: 'all',
  });

  // Load product categories for filter
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await productsService.getProductCategories({
          page: 1,
          perPage: 1000, // Fetch all categories for dropdown
        });
        const categories = Array.isArray(categoriesData) ? categoriesData : [];
        setCategories(categories);
        console.log(
          `✅ Loaded ${categories.length} categories (total: ${
            (categoriesData as any).__total || categories.length
          })`
        );
      } catch (err) {
        console.error('Failed to load product categories:', err);
      }
    };
    loadCategories();
  }, []);

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to page 1 when search changes
      if (searchTerm !== debouncedSearchTerm) {
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, debouncedSearchTerm]);

  // Build filter object for API
  const buildFilterObject = useCallback(() => {
    const filterObj: any = {};

    if (filters.categoryId) {
      filterObj.categoryId = filters.categoryId;
    }

    if (filters.recipeType === 'recipe') {
      filterObj.recipeId = { $exists: true, $ne: null };
    } else if (filters.recipeType === 'stock') {
      filterObj.recipeId = { $exists: false };
    }

    if (filters.currency) {
      filterObj.currency = filters.currency;
    }

    if (filters.isActive !== 'all' && filters.isActive !== undefined) {
      filterObj.isActive = filters.isActive;
    }

    return Object.keys(filterObj).length > 0 ? filterObj : undefined;
  }, [filters]);

  // Fetch products - follows OrdersReport pattern with retry logic
  const handleFetchProducts = useCallback(
    async (retryCount = 0) => {
      const maxRetries = 3;
      const currentPage = pagination.page;
      const currentPerPage = pagination.perPage;
      const currentSearch = debouncedSearchTerm;
      const currentFilter = buildFilterObject();

      try {
        const response = await fetchData(async () => {
          console.log(
            `🔄 Loading products page ${currentPage} (${currentPerPage} per page)...`,
            { search: currentSearch, filter: currentFilter }
          );

          const result = await productsService.getProducts({
            page: currentPage,
            size: currentPerPage,
            q: currentSearch || undefined,
            filter: currentFilter,
          });

          const productsArray = Array.isArray(result) ? result : [];
          const total = (result as any).__total ?? 0;

          console.log(
            `✅ Loaded ${productsArray.length} products (page ${currentPage}, total: ${total})`
          );

          return {
            products: productsArray,
            total,
          };
        });

        if (response) {
          setProducts(response.products);
          setPagination((prev) => ({ ...prev, total: response.total }));
          hasLoadedOnceRef.current = true;
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load products';
        console.error(
          `❌ Failed to load products (attempt ${retryCount + 1}/${
            maxRetries + 1
          }):`,
          err
        );

        // Retry on network errors
        if (
          retryCount < maxRetries &&
          (errorMessage.includes('Failed to fetch') ||
            errorMessage.includes('ERR_CONNECTION_REFUSED') ||
            errorMessage.includes('NetworkError'))
        ) {
          console.log(`🔄 Retrying in ${(retryCount + 1) * 1000}ms...`);
          await new Promise((resolve) =>
            setTimeout(resolve, (retryCount + 1) * 1000)
          );
          return handleFetchProducts(retryCount + 1);
        }

        setProducts([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      }
    },
    [
      pagination.page,
      pagination.perPage,
      debouncedSearchTerm,
      buildFilterObject,
      fetchData,
    ]
  );

  // Auto-fetch data when component mounts or dependencies change
  useEffect(() => {
    handleFetchProducts();
  }, [
    pagination.page,
    pagination.perPage,
    debouncedSearchTerm,
    filters,
    refreshTrigger,
    handleFetchProducts,
  ]);

  const handleSearch = () => {
    // Immediately update debounced search and reset to page 1
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setDebouncedSearchTerm(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(pagination.total / pagination.perPage)
    ) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilterForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    const newFilters: typeof filters = {
      categoryId:
        filterForm.categoryId === 'all' ? undefined : filterForm.categoryId,
      recipeType:
        filterForm.recipeType === 'all'
          ? 'all'
          : (filterForm.recipeType as 'recipe' | 'stock'),
      currency: filterForm.currency === 'all' ? undefined : filterForm.currency,
      isActive:
        filterForm.isActive === 'all' ? 'all' : filterForm.isActive === 'true',
    };

    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to page 1
    setShowFilterModal(false);
  };

  const handleClearFilters = () => {
    setFilterForm({
      categoryId: 'all',
      recipeType: 'all',
      currency: 'all',
      isActive: 'all',
    });
    setFilters({
      categoryId: undefined,
      recipeType: 'all',
      currency: undefined,
      isActive: 'all',
    });
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to page 1
    setShowFilterModal(false);
  };

  const handleDeleteClick = (product: Product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    try {
      await productsService.deleteProduct(productToDelete.id);
      notify(
        translate('pos.products.delete_success', 'Product deleted successfully'),
        { type: 'success' }
      );
      // Remove the product from the list immediately
      setProducts(products.filter((p) => p.id !== productToDelete.id));
      // Update total count
      setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      // Close dialog and reset
      setDeleteDialogOpen(false);
      setProductToDelete(null);
      // Refresh the list to ensure consistency
      setTimeout(() => {
        handleFetchProducts();
      }, 500);
    } catch (error) {
      console.error('Error deleting product:', error);
      let errorMessage = translate('pos.products.delete_error', 'Failed to delete product. Please try again.');
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      // Always show error notification
      notify(errorMessage, { type: 'error', duration: 7000 });
      
      // Close dialog on error so user can see the notification and try again if needed
      setDeleteDialogOpen(false);
      setProductToDelete(null);
    }
  };

  // Count active filters
  const activeFilterCount =
    (filters.categoryId ? 1 : 0) +
    (filters.recipeType !== 'all' ? 1 : 0) +
    (filters.currency ? 1 : 0) +
    (filters.isActive !== 'all' ? 1 : 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between -mx-4 px-4">
        <div className="flex items-center space-x-3">
          <Package className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">
            {translate('pos.products.name', 'Products')}
          </h1>
        </div>
        {canCreate && (
          <Button
            onClick={onCreateClick}
            className="flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>{translate('common.add', 'Add')}</span>
          </Button>
        )}
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                ref={searchInputRef}
                placeholder={translate('common.search', 'Search...')}
                value={searchTerm}
                onChange={(e) => {
                  const wasFocused =
                    document.activeElement === searchInputRef.current;
                  setSearchTerm(e.target.value);
                  // Restore focus if it was focused before
                  if (wasFocused && searchInputRef.current) {
                    setTimeout(() => {
                      searchInputRef.current?.focus();
                    }, 0);
                  }
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
            <Button onClick={handleSearch}>
              {translate('common.search', 'Search')}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                // Sync filter form with current filters when opening modal
                setFilterForm({
                  categoryId: filters.categoryId || 'all',
                  recipeType: filters.recipeType || 'all',
                  currency: filters.currency || 'all',
                  isActive:
                    filters.isActive === 'all'
                      ? 'all'
                      : filters.isActive === true
                      ? 'true'
                      : 'false',
                });
                setShowFilterModal(true);
              }}
              className="relative"
            >
              <Filter className="h-4 w-4 mr-2" />
              {translate('common.filter', 'Filter')}
              {activeFilterCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-primary text-primary-foreground rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Loading and Error States - matches OrdersReport pattern */}
      <ListFetchStates
        isLoading={isLoading}
        isError={isError}
        onRetry={handleFetchProducts}
        loadingMessage={translate('common.loading', 'Loading products...')}
        errorMessage={translate(
          'pos.products.error_loading',
          'Failed to load products'
        )}
      />

      {/* Products Table - only show when not loading and no error */}
      {!isLoading && !isError && (
        <Card>
          <CardHeader>
            <CardTitle>
              {translate('pos.products.name', 'Products')}
              {pagination.total > 0 && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  ({pagination.total} {translate('common.total', 'total')})
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {products.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {debouncedSearchTerm
                  ? translate(
                      'common.noData',
                      'No products found matching your search'
                    )
                  : translate('common.noData', 'No data found')}
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        {translate('pos.products.fields.name', 'Name')}
                      </TableHead>
                      <TableHead>
                        {translate('pos.products.fields.price', 'Price')}
                      </TableHead>
                      <TableHead>
                        {translate(
                          'pos.products.fields.currency',
                          'Currency'
                        )}
                      </TableHead>
                      <TableHead>
                        {translate(
                          'pos.products.fields.categoryId',
                          'Category'
                        )}
                      </TableHead>
                      <TableHead>
                        {translate(
                          'pos.products.fields.recipeId',
                          'Recipe'
                        )}
                      </TableHead>
                      <TableHead>
                        {translate('common.actions', 'Actions')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell className="font-medium">
                          {(product as any).name || '-'}
                        </TableCell>
                        <TableCell>
                          <span data-sensitive-field="amount">
                          {product.price?.toLocaleString() || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {(product as any).currency || 'VND'}
                        </TableCell>
                        <TableCell>
                          {product.categoryId
                            ? categories.find((c) => c.id === product.categoryId)
                                ?.name || '-'
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {product.recipeId ? (
                            <Badge variant="outline">
                              {translate('pos.products.recipe_based')}
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              {translate('pos.products.stock_based')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onEditClick(product)}
                                title={translate('pos.common.action.edit', 'Edit')}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (!canDelete) {
                                  notify(
                                    translate(
                                      'pos.common.accessDenied',
                                      'You do not have permission to delete products'
                                    ),
                                    { type: 'error' }
                                  );
                                  return;
                                }
                                handleDeleteClick(product);
                              }}
                              disabled={!canDelete}
                              className={
                                canDelete
                                  ? 'text-destructive hover:text-destructive'
                                  : 'opacity-50 cursor-not-allowed'
                              }
                              title={
                                canDelete
                                  ? translate('pos.common.action.delete', 'Delete')
                                  : translate(
                                      'pos.common.accessDenied',
                                      'You do not have permission to delete products'
                                    )
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination Controls */}
                {pagination.total > 0 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between mt-4 pt-4 border-t gap-4">
                    <div className="text-sm text-muted-foreground">
                      {translate('pos.orders.modern.pagination.showing', {
                        start: (pagination.page - 1) * pagination.perPage + 1,
                        end: Math.min(
                          pagination.page * pagination.perPage,
                          pagination.total
                        ),
                        total: pagination.total,
                      })}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1 || isLoading}
                        className="flex items-center gap-1"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        {translate(
                          'pos.orders.modern.pagination.previous',
                          'Previous'
                        )}
                      </Button>
                      <div className="text-sm text-muted-foreground px-2">
                        {translate('common.page', 'Page')} {pagination.page} /{' '}
                        {Math.ceil(pagination.total / pagination.perPage)}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={
                          pagination.page * pagination.perPage >=
                            pagination.total || isLoading
                        }
                        className="flex items-center gap-1"
                      >
                        {translate(
                          'pos.orders.modern.pagination.next',
                          'Next'
                        )}
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{translate('common.filters', 'Filters')}</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => setShowFilterModal(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Category Filter */}
              <div className="space-y-2">
                <Label>
                  {translate(
                    'pos.products.fields.categoryId',
                    'Category'
                  )}
                </Label>
                <Select
                  value={filterForm.categoryId}
                  onValueChange={(value) =>
                    handleFilterChange('categoryId', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={translate('common.all', 'All')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {translate('common.all', 'All')}
                    </SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Recipe Type Filter */}
              <div className="space-y-2">
                <Label>
                  {translate(
                    'pos.products.fields.recipeId',
                    'Recipe Type'
                  )}
                </Label>
                <Select
                  value={filterForm.recipeType}
                  onValueChange={(value) =>
                    handleFilterChange('recipeType', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {translate('common.all', 'All')}
                    </SelectItem>
                    <SelectItem value="recipe">
                      {translate('pos.products.recipe_based')}
                    </SelectItem>
                    <SelectItem value="stock">
                      {translate('pos.products.stock_based')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Currency Filter */}
              <div className="space-y-2">
                <Label>
                  {translate('pos.products.fields.currency', 'Currency')}
                </Label>
                <Select
                  value={filterForm.currency}
                  onValueChange={(value) =>
                    handleFilterChange('currency', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder={translate('common.all', 'All')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {translate('common.all', 'All')}
                    </SelectItem>
                    <SelectItem value="VND">VND</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Active Status Filter */}
              <div className="space-y-2">
                <Label>{translate('common.status', 'Status')}</Label>
                <Select
                  value={filterForm.isActive}
                  onValueChange={(value) =>
                    handleFilterChange('isActive', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {translate('common.all', 'All')}
                    </SelectItem>
                    <SelectItem value="true">
                      {translate('common.active', 'Active')}
                    </SelectItem>
                    <SelectItem value="false">
                      {translate('common.inactive', 'Inactive')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleClearFilters}
                  className="flex-1"
                >
                  {translate('pos.common.action.clear', 'Clear')}
                </Button>
                <Button onClick={handleApplyFilters} className="flex-1">
                  {translate('pos.common.action.apply', 'Apply')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate('pos.products.delete_title', 'Delete Product')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {productToDelete
                ? translate(
                    'pos.products.delete_confirm',
                    'Are you sure you want to delete this product? This action cannot be undone.'
                  )
                : translate(
                    'pos.products.delete_confirm',
                    'Are you sure you want to delete this product? This action cannot be undone.'
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDeleteDialogOpen(false);
                setProductToDelete(null);
              }}
            >
              {translate('common.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {translate('pos.common.action.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
